<template>
  <TriadModal ref="modalRef" :title="resourceType === 'artist' ? $t('report.titleArtist') : $t('report.title')" size="medium" class="!z-[500]">
    <!-- Header info if resourceTitle is present -->
    <div v-if="resourceTitle" class="-mt-2 mb-3">
      <p class="text-xs font-medium text-indigo-600 dark:text-indigo-400 truncate">
        {{ resourceTitle }} <span class="text-gray-400 font-normal">({{ resourceId }})</span>
      </p>
    </div>

    <!-- Success State -->
    <div v-if="submitted" class="py-6 text-center space-y-3">
      <div class="flex items-center justify-center w-14 h-14 rounded-full bg-green-100 dark:bg-green-900/30 mx-auto">
        <svg class="w-7 h-7 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5" />
        </svg>
      </div>
      <h4 class="text-base font-semibold text-gray-900 dark:text-white">{{ $t('report.successTitle') }}</h4>
      <p class="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto leading-relaxed">{{ $t('report.successMessage') }}</p>
      <div class="pt-2">
        <button
          type="button"
          @click="close"
          class="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer"
        >
          {{ $t('report.close') }}
        </button>
      </div>
    </div>

    <!-- Form State -->
    <form v-else @submit.prevent="submitReport" class="space-y-4">
      <!-- Disclaimer -->
      <div class="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
        {{ $t('report.disclaimer') }}
      </div>

      <!-- Reason -->
      <div>
        <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
          {{ $t('report.reasonLabel') }} <span class="text-red-500">*</span>
        </label>
        <select
          v-model="form.reason"
          required
          class="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
        >
          <option value="" disabled>{{ $t('report.reasonPlaceholder') }}</option>
          <template v-if="resourceType === 'artist'">
            <option value="inaccurate_info">{{ $t('report.reasons.inaccurateInfo') }}</option>
            <option value="impersonation">{{ $t('report.reasons.impersonation') }}</option>
            <option value="offensive_content">{{ $t('report.reasons.offensiveContent') }}</option>
            <option value="spam">{{ $t('report.reasons.spam') }}</option>
            <option value="other">{{ $t('report.reasons.other') }}</option>
          </template>
          <template v-else>
            <option value="unauthorized_lyrics">{{ $t('report.reasons.unauthorizedLyrics') }}</option>
            <option value="unauthorized_chords">{{ $t('report.reasons.unauthorizedChords') }}</option>
            <option value="unauthorized_composition">{{ $t('report.reasons.unauthorizedComposition') }}</option>
            <option value="impersonation">{{ $t('report.reasons.impersonation') }}</option>
            <option value="offensive_content">{{ $t('report.reasons.offensiveContent') }}</option>
            <option value="spam">{{ $t('report.reasons.spam') }}</option>
            <option value="other">{{ $t('report.reasons.other') }}</option>
          </template>
        </select>
      </div>

      <!-- Claimant Name -->
      <div>
        <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
          {{ $t('report.claimantLabel') }} <span class="text-red-500">*</span>
        </label>
        <input
          v-model="form.claimantName"
          required
          type="text"
          :placeholder="$t('report.claimantPlaceholder')"
          class="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
        />
      </div>

      <!-- Contact Email -->
      <div>
        <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
          {{ $t('report.contactEmailLabel') }} <span class="text-red-500">*</span>
        </label>
        <input
          v-model="form.contactEmail"
          required
          type="email"
          :placeholder="$t('report.contactEmailPlaceholder')"
          class="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
        />
      </div>

      <!-- Description -->
      <div>
        <label class="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1.5">
          {{ $t('report.descriptionLabel') }}
        </label>
        <textarea
          v-model="form.description"
          rows="3"
          :placeholder="$t('report.descriptionPlaceholder')"
          class="w-full text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2.5 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition resize-none"
        />
      </div>

      <!-- Good-faith oath checkbox -->
      <div class="flex items-start gap-2.5">
        <input
          id="report-oath"
          v-model="form.oath"
          type="checkbox"
          required
          class="mt-0.5 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 cursor-pointer shrink-0"
        />
        <label for="report-oath" class="text-xs text-gray-600 dark:text-gray-400 leading-relaxed cursor-pointer">
          {{ $t('report.oathText') }}
        </label>
      </div>

      <!-- Actions -->
      <div class="flex gap-3 pt-2">
        <button
          type="button"
          @click="close"
          class="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors cursor-pointer"
        >
          {{ $t('report.cancel') }}
        </button>
        <button
          type="submit"
          :disabled="!canSubmit || submitting"
          class="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors flex items-center justify-center gap-2 cursor-pointer"
        >
          <svg v-if="submitting" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
          </svg>
          {{ submitting ? $t('report.submitting') : $t('report.submit') }}
        </button>
      </div>
    </form>
  </TriadModal>
</template>

<script setup>
import { ref, computed, reactive, defineExpose } from 'vue';
import TriadModal from '@/components/common/TriadModal.vue';

const modalRef = ref(null);

const resourceId = ref('');
const resourceTitle = ref('');
const resourceType = ref('song'); // 'song' | 'artist'

// Aliases for backward compatibility
const songId = resourceId;
const songTitle = resourceTitle;

const submitted = ref(false);
const submitting = ref(false);

const form = reactive({
  reason: '',
  claimantName: '',
  contactEmail: '',
  description: '',
  oath: false,
});

const canSubmit = computed(() =>
  form.reason && form.claimantName && form.contactEmail && form.oath
);

const resetForm = () => {
  form.reason = '';
  form.claimantName = '';
  form.contactEmail = '';
  form.description = '';
  form.oath = false;
  submitted.value = false;
  submitting.value = false;
};

const open = (id, title, type = 'song') => {
  resourceId.value = id || '';
  resourceTitle.value = title || '';
  resourceType.value = type || 'song';
  resetForm();
  modalRef.value?.open();
};

const close = () => {
  modalRef.value?.close();
};

const submitReport = async () => {
  if (!canSubmit.value) return;
  submitting.value = true;

  try {
    const isArtist = resourceType.value === 'artist';
    const typeLabel = isArtist ? 'Artist' : 'Song';
    const reportTitle = isArtist ? 'Artist / UGC Content Report' : 'DMCA/Copyright Infringement Report';
    const subject = encodeURIComponent(`[${isArtist ? 'Artist Report' : 'DMCA Report'}] ${typeLabel}: ${resourceTitle.value} (ID: ${resourceId.value})`);
    const body = encodeURIComponent(
      `${reportTitle}\n` +
      `====================================\n` +
      `${typeLabel} Title/Name: ${resourceTitle.value}\n` +
      `${typeLabel} ID: ${resourceId.value}\n` +
      `Reason: ${form.reason}\n` +
      `Claimant Name: ${form.claimantName}\n` +
      `Contact Email: ${form.contactEmail}\n` +
      `\nDescription:\n${form.description || '(not provided)'}\n` +
      `\nGood-faith oath acknowledged: YES\n` +
      `\nSubmitted at: ${new Date().toISOString()}`
    );

    const endpoint = isArtist ? '/api/artist/report' : '/api/song/report-copyright';
    const payload = isArtist
      ? {
          artistId: resourceId.value,
          artistName: resourceTitle.value,
          reason: form.reason,
          claimantName: form.claimantName,
          contactEmail: form.contactEmail,
          description: form.description,
          oath: form.oath,
        }
      : {
          songId: resourceId.value,
          songTitle: resourceTitle.value,
          reason: form.reason,
          claimantName: form.claimantName,
          contactEmail: form.contactEmail,
          description: form.description,
          oath: form.oath,
        };

    let apiSuccess = false;
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      apiSuccess = res.ok;
    } catch (_) {
      apiSuccess = false;
    }

    if (!apiSuccess) {
      window.open(`mailto:dmca@triadmusic.app?subject=${subject}&body=${body}`, '_blank');
    }

    submitted.value = true;
  } finally {
    submitting.value = false;
  }
};

defineExpose({ open, close, songId, songTitle, resourceId, resourceTitle, resourceType });
</script>
